/* eslint-disable react-hooks/exhaustive-deps */
import { RootState } from 'App'
import {
  BusinessGoalDateFilter,
  BusinessGoalPriority,
  UserTask,
  UserTaskItemStatus,
  UserTaskQuickFilter,
  UserTasksFilterInput,
  useGetUserTasksQuery,
} from '__generated__/api-types-and-hooks'
import { ITaskSummaryItem } from 'components/AppDashboard/interfaces'
import { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getTenantId } from 'utils/getTenantId'

interface IUseGetUserTasksInput {
  filter?: BusinessGoalDateFilter
  userId?: string
  quickFilter?: UserTaskQuickFilter
  assigneeId?: string
}

export const useGetUserTasks = (input?: IUseGetUserTasksInput) => {
  const tenantId = getTenantId()
  const clientId = useSelector((state: RootState) => state.clients.client.profile.id)
  const [userTasks, setUserTasks] = useState<UserTask[]>([])
  const filter = { dateRange: input?.filter } as UserTasksFilterInput
  if (input?.quickFilter) {
    filter.quickFilter = input.quickFilter
  }
  if (input?.assigneeId) {
    filter.assigneeId = [input.assigneeId]
  }
  const {
    data,
    isLoading: isUserTasksLoading,
    isError,
    refetch: refetchUserTasks,
  } = useGetUserTasksQuery(
    {
      input: { tenantId, userId: input?.userId ?? clientId, filter },
    },
    { refetchOnWindowFocus: false }
  )

  useEffect(() => {
    if (data && data.getUserTasks) {
      setUserTasks(data.getUserTasks as unknown as UserTask[])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const filterTaskByStatus = (UserTasks: UserTask[], status: UserTaskItemStatus) => {
    const tasks = userTasks.filter((task) => task.status === status)
    return tasks.map((task) => ({
      ...task,
      priorityLabel: task.taskPriority?.split('_').join(' ').toLowerCase() ?? '',
      priorityStatus: task.taskPriority ?? BusinessGoalPriority.MediumPriority,
    }))
  }

  const todoTasks: ITaskSummaryItem[] = useMemo(
    () => filterTaskByStatus(userTasks, UserTaskItemStatus.Todo),
    [userTasks]
  )
  const inProgressTasks: ITaskSummaryItem[] = useMemo(
    () => filterTaskByStatus(userTasks, UserTaskItemStatus.InProgress),
    [userTasks]
  )
  const blockedTasks: ITaskSummaryItem[] = useMemo(
    () => filterTaskByStatus(userTasks, UserTaskItemStatus.Blocked),
    [userTasks]
  )
  const completedTasks: ITaskSummaryItem[] = useMemo(
    () => filterTaskByStatus(userTasks, UserTaskItemStatus.Done),
    [userTasks]
  )
  const uncompletedTasks: ITaskSummaryItem[] = useMemo(() => {
    const tasks = userTasks.filter((task) => task.status !== UserTaskItemStatus.Done)
    return tasks.map((task) => ({
      ...task,
      priorityLabel: task.taskPriority?.split('_').join(' ').toLowerCase() ?? '',
      priorityStatus: task.taskPriority!,
    }))
  }, [userTasks])

  return {
    userTasks,
    todoTasks,
    inProgressTasks,
    blockedTasks,
    completedTasks,
    uncompletedTasks,
    isUserTasksLoading,
    refetchUserTasks,
    // The fetcher returns partial data ({ getUserTasks: null }) on a field error instead of throwing
    isUserTasksError: isError || (!!data && data.getUserTasks == null),
  }
}
